/**
 * REF-2 — the Refund Log's real read path (GET /sales/live/v2/refund-log), via Redux.
 *
 * This is the ONE real-data accessor for the period refund component vector — every screen
 * that needs `{ summary, records }` for a date range (Snapshot, Sales, EOD, the REF-2
 * drill-in, DrillTips, Sales by Channel breakdown) reads it here, so none of them can drift
 * from another or from stale sample data.
 *
 * `summary` is always derived from `records` via `summarizeRefunds`, never from the API's
 * own top-level totals: the FE's approximated component split (see refundLogRow.ts) must
 * stay internally consistent, and mixing in a differently-rounded server total would let
 * the context strip disagree with the log rows underneath it.
 */
import { useEffect, useMemo } from 'react';
import { useBranch } from '../../../context/BranchContext';
import { useAuth } from '../../../context/AuthContext';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchRefundLogRequest } from '../../../redux/reports/clickHouse/clickHouseActions';
import type { OrderSource } from '../../../redux/reports/clickHouse/clickHouseApi';
import { summarizeRefunds, type RefundPeriodSummary, type RefundRecord, type RefundSalesSource } from './refundModel';
import { refundLogItemsToRecords } from './refundLogRow';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const toOrderSource = (source: RefundSalesSource): OrderSource | undefined =>
    source === 'direct' ? 'direct' : source === 'thirdparty' ? 'thirdparty' : undefined;

export interface UseRefundLogPeriodArgs {
    startDate?: string;
    endDate?: string;
    /** Defaults to 'overall'. */
    source?: RefundSalesSource;
}

export interface UseRefundLogPeriodResult {
    summary: RefundPeriodSummary;
    records: RefundRecord[];
    loading: boolean;
    failure: boolean;
    retry: () => void;
}

export function useRefundLogPeriod({ startDate, endDate, source = 'overall' }: UseRefundLogPeriodArgs): UseRefundLogPeriodResult {
    const dispatch = useAppDispatch();
    const { selectedBranch: branch } = useBranch();
    const { auth } = useAuth();
    const locationId = branch?.id ?? branch?.locationId ?? '';
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);

    const orderSource = toOrderSource(source);

    const fetch = () => {
        // `locationId` (branch context) and `token` (auth context) resolve independently on
        // mount — gating on locationId alone fires once with token still null, then again
        // once auth catches up, double-hitting the API. Same guard useEodSummaryData.ts uses.
        if (!locationId || !token) return;
        dispatch(fetchRefundLogRequest(locationId, startDate, endDate, orderSource));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetch(); }, [dispatch, locationId, startDate, endDate, orderSource, token]);

    const rawItems = useAppSelector((s) => s.clickHouse.refundLogSuccess);
    const loading = useAppSelector((s) => s.clickHouse.refundLogLoading);
    const failure = useAppSelector((s) => s.clickHouse.refundLogFailure);

    const records = useMemo(() => refundLogItemsToRecords(rawItems), [rawItems]);
    const summary = useMemo(() => summarizeRefunds(records), [records]);

    return { summary, records, loading, failure, retry: fetch };
}
